import {
    Math as CesiumMath,
    Cesium3DTileFeature,
    Cartesian2,
    Cartesian3,
    Cartographic,
    SceneTransforms,
    defined,
} from 'cesium';
import './index.less';
import util from './util';

/**
 * 信息工具。
 *
 * @alias InfoTool
 * @constructor
 * @param {viewer} viewer Cesium视窗。
 */
class InfoTool {
    /**
     * 创建一个动态实体弹窗。
     *
     * @param {viewer} viewer Cesium视窗。
     * @param {Number} options 选项。
     * @param {Cartesian3} options.position 弹出位置。
     * @param {HTMLElement} options.element 弹出窗元素容器。
     * @param {Function} callback 回调函数。
     * @ignore
     */
    static #createInfoTool(viewer, options, callback = undefined) {
        const cartographic = Cartographic.fromCartesian(options.position);
        const lon = CesiumMath.toDegrees(cartographic.longitude); //.toFixed(5);
        const lat = CesiumMath.toDegrees(cartographic.latitude); //.toFixed(5);

        // 回调
        callback();

        // 添加div弹窗
        setTimeout(function() {
            InfoTool.#popup(
                viewer,
                options.element,
                lon,
                lat,
                cartographic.height,
                // options.geometryType,
                options.offset,
            );
        }, 100);
    }

    /**
     * 弹出HTML元素弹窗。
     * @param {viewer} viewer Cesium视窗。
     * @param {Element|HTMLElement} element 弹窗元素。
     * @param {Number} lon 经度。
     * @param {Number} lat 纬度。
     * @param {Number} height 高度。
     * @ignore
     */
    static #popup(viewer, element, lon, lat, height, offset) {
        setTimeout(function() {
            // 设置元素效果
            util.setCss(element, 'opacity', '1');
            util.setCss(element, 'z-index', '0');
        }, 100);
        const divPosition = Cartesian3.fromDegrees(lon, lat, height);

        let x = offset?.x || 0;
        let y = offset?.y || 0;

        InfoTool.#hookToGlobe(
            viewer,
            element,
            divPosition,
            [-4 + x, y],
            true,
        );
        viewer.scene.requestRender();
    }

    /**
     * 将HTML弹窗挂接到地球上。
     *
     * @param {viewer} viewer Cesium视窗。
     * @param {Element} element 弹窗元素。
     * @param {Cartesian3} position 地图坐标点。
     * @param {Array} offset 偏移。
     * @param {Boolean} hideOnBehindGlobe 当元素在地球背面会自动隐藏，以减轻判断计算压力。
     * @ignore
     */
    static #hookToGlobe(viewer, element, position, offset, hideOnBehindGlobe) {
        const scene = viewer.scene,
            camera = viewer.camera;
        const cartesian2 = new Cartesian2();
        const height = viewer._lastHeight;

        let _style = {};
        scene.preRender.addEventListener(function () {
            const canvasPosition = scene.cartesianToCanvasCoordinates(position, cartesian2); // 笛卡尔坐标到画布坐标

            if (defined(canvasPosition)) {
                const left = parseInt(canvasPosition.x + offset[0]);
                const bottom = parseInt(height - canvasPosition.y + offset[1]);

                if (_style.left) {
                    _style.left = ((_style.left - left > 1) || (_style.left - left < -1)) ? left : _style.left;
                } else {
                    _style.left = left;
                }

                _style.bottom = bottom;
                util.setCss(element, 'left', _style.left + 'px');
                util.setCss(element, 'bottom', _style.bottom + 'px');


                // 是否在地球背面隐藏
                if (hideOnBehindGlobe) {
                    const cameraPosition = camera.position;
                    let height = scene.globe.ellipsoid.cartesianToCartographic(cameraPosition)
                        .height;
                    height += scene.globe.ellipsoid.maximumRadius;
                    if (!(Cartesian3.distance(cameraPosition, position) > height)) {
                        // util.setCss(element, 'display', 'flex');
                    } else {
                        // util.setCss(element, 'display', 'none');
                    }
                }
            }
        });
    }

    #element;
    viewer;

    constructor(options) {
        this.viewer = options.map;
        // 在Cesium容器中添加元素
        this.#element = document.createElement('div');
    }

    /**
     * 添加。
     * @param {Object} options 选项。
     * @param {Element} options.element 弹窗元素。
     * @param {Cartesian2|Cartesian3} options.position 点击位置。
     * @param {Cesium3DTileFeature} [options.inputFeature] 模型要素。
     * @param {String} options.type 类型（默认值为default，即任意点击模式；如果设置为info，即信息模式，只有点击Feature才会响应）。
     * @param {String} options.content 内容（只有类型为default时才起作用）。
     * @param {Function} callback 回调函数。
     */
    add(options, callback = undefined) {
        this.element = options.element;
        // 判断参数为空返回
        if (!options) {
            return;
        }
        // 点
        let position, cartesian2d, cartesian3d, inputFeature;
        if (options instanceof Cesium3DTileFeature) {
            inputFeature = options;
            options = {};
        } else {
            if (options instanceof Cartesian2 || options instanceof Cartesian3) {
                position = options;
                options = {};
            } else {
                position = options.position;
                inputFeature = options.inputFeature;
            }
            // 判断点位为空返回
            if (!position) {
                return;
            }
            if (position instanceof Cartesian2) {
                // 二维转三维
                // 如果支持拾取模型则取模型值
                cartesian3d =
                    this.viewer.scene.pickPositionSupported &&
                    defined(this.viewer.scene.pick(options.position))
                        ? this.viewer.scene.pickPosition(position)
                        : this.viewer.camera.pickEllipsoid(
                              position,
                              this.viewer.scene.globe.ellipsoid,
                          );
                cartesian2d = position;
            } else {
                cartesian3d = position;
                cartesian2d = SceneTransforms.wgs84ToWindowCoordinates(
                    this.viewer.scene,
                    cartesian3d,
                );
            }
            // 判断点位为空返回
            if (!cartesian3d) {
                return;
            }
        }

        const that = this;

        // 1.组织信息
        let info = '';
        if (options.type === 'info') {
            // 拾取要素
            const feature = inputFeature || this.viewer.scene.pick(cartesian2d);
            // 判断拾取要素为空返回
            if (!defined(feature)) {
                this.remove();
                return;
            }

            if (feature instanceof Cesium3DTileFeature) {
                // 3dtiles
                let propertyNames = feature?.getPropertyNames?.() || [];
                let length = propertyNames.length;
                for (let i = 0; i < length; ++i) {
                    let propertyName = propertyNames[i];
                    info +=
                        '"' + (propertyName + '": "' + feature.getProperty(propertyName)) + '",\n';
                }
            } else if (feature) {
                // Entity
                const properties = feature.properties;
                if (properties) {
                    let propertyNames = properties._propertyNames;
                    let length = propertyNames.length;
                    for (let i = 0; i < length; ++i) {
                        let propertyName = propertyNames[i];
                        info +=
                            '"' +
                            (propertyName + '": "' + properties[propertyName]._value) +
                            '",\n';
                    }
                }
            }
        } else {
            options.content && (info = options.content);
        }

        // 2.生成特效
        // 添加之前先移除
        this.remove();
        // if (!info) {
        //   return;
        // }
        options.position = cartesian3d;
        options.element = options.element || this.#element;

        InfoTool.#createInfoTool(this.viewer, options, function() {
            util.setInnerText(options.element, info);
            typeof callback === 'function' && callback();
        });
    }

    show() {
        util.setCss(this.element, 'opacity', '1');
        util.setCss(this.element, 'z-index', '1');
        util.setCss(this.#element, 'opacity', '1');
    }

    /**
     * 移除。
     */
    remove() {
        util.setCss(this.element, 'opacity', '0');
        util.setCss(this.element, 'z-index', '-1');
        util.setCss(this.#element, 'opacity', '0');
    }
}

export default InfoTool;
